import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import { getApiBaseUrl } from './route-utils'
import { RequestParameters } from './client-api'
import * as R from 'ramda'
import versionData from '../../app/common/version.json'
import { t } from 'i18next'
import { ApiError, AppErrorType } from '../common-error-utils'

type AxiosMethod = 'GET' | 'POST'

const createApiClient = () => {
  const axiosInstance = axios.create({ baseURL: getApiBaseUrl(), timeout: 90000 })
  axiosInstance.interceptors.response.use(response => response, handleAxiosError)

  const get = async <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    const response: AxiosResponse<T> = await axiosInstance.get(url, config)
    return response.data
  }

  const post = async <T = any, D = any>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> => {
    const response: AxiosResponse<T> = await axiosInstance.post(url, data, config)
    return response.data
  }

  return { get, post }
}

const handleAxiosError = (error: any) => {
  if (error.response) {
    const { response } = error
    const { status, data } = response

    if (error.code === 'ECONNABORTED') {
      return Promise.reject(createApiError(AppErrorType.TimeoutError, 'Request timeout : ECONNABORTED', status))
    }

    const body = (data?.error ?? data) ?? {}
    const clientReqId = (body as any)?.clientReqId
    const cause = (body as any)?.cause
    switch (status) {
      case 401:
        // type must stay INVALID_SESSION regardless of the underlying error's name (e.g. nested SalesforceError),
        // otherwise session-expiry detection downstream (isInvalidSession checks) breaks
        throw createApiError(AppErrorType.INVALID_SESSION, AppErrorType.INVALID_SESSION, status, body.name, clientReqId, cause)
      case 412:
        return Promise.reject(
          createApiError(body.name || AppErrorType.VersionMismatch, 'Version mismatch error', status, undefined, clientReqId, cause)
        )

      case 500:
        return Promise.reject(
          createApiError(
            body.name || AppErrorType.InternalServerError,
            body?.message || '500 Internal server error',
            status,
            body?.name,
            clientReqId,
            cause
          )
        )

      default:
        if (typeof body === 'object' && body.name === 'SalesforceError') {
          const permissionError = 'Please reference your WSDL or the describe call for the appropriate names.'
          if (body.message?.includes(permissionError)) {
            return Promise.reject(
              createApiError(
                body.name || AppErrorType.SalesforcePermissionError,
                body.message,
                status,
                'SalesforceError',
                clientReqId,
                cause
              )
            )
          }
          return Promise.reject(
            createApiError(
              body.name || AppErrorType.SalesforceError,
              body.message || 'Salesforce error',
              status,
              'SalesforceError',
              clientReqId,
              cause
            )
          )
        }

        if (body?.message?.includes('no cached data available for')) {
          return Promise.reject(createApiError(body.name || AppErrorType.CacheExpired, body.message, status, undefined, clientReqId, cause))
        }

        if (body != null && body.name === AppErrorType.ValidationError) {
          return Promise.reject(
            createApiError(AppErrorType.ValidationError, body.message || 'Validation error', status, body.name)
          )
        }

        if (body != null && (!R.isEmpty(body.name) || !R.isEmpty(body.message))) {
          return Promise.reject(
            createApiError(
              body.name || body?.name || AppErrorType.UnknownError,
              body?.message || 'Unknown error occurred',
              status,
              body?.name || t('errors.errorTitle'),
              clientReqId,
              cause
            )
          )
        }

        return Promise.reject(
          createApiError(body.name || AppErrorType.NetworkError, 'No response from server', status, undefined, clientReqId, cause)
        )
    }
  }

  if (error.request && !error.response) {
    return Promise.reject(createApiError(AppErrorType.NetworkError, 'No response from server'))
  }

  return Promise.reject(createApiError(AppErrorType.UnknownError, 'An unexpected error occurred'))
}

const createApiError = (
  type: AppErrorType,
  message: string,
  status?: number,
  name?: string,
  clientReqId?: string,
  cause?: Error | string
): ApiError => new ApiError(type, message, status, name, cause, clientReqId)

export const axiosGet = async <T>(params: RequestParameters): Promise<T> => await makeAxiosRequest('GET', params)

export const axiosPost = async <T>(params: RequestParameters): Promise<T> => await makeAxiosRequest<T>('POST', params)

let apiClient: ReturnType<typeof createApiClient> | null = null
const getApiClient = () => {
  if (!apiClient) {
    apiClient = createApiClient()
  }
  return apiClient
}

const makeAxiosRequest = async <T>(method: AxiosMethod, params: RequestParameters): Promise<T> => {
  const { path, data, timeout = 90000, contentType, cacheControl, signal: ClientSignal } = params
  const currentPath = () => location.href.replace(location.origin, '')

  const headers = R.mergeAll([
    { path: currentPath(), version: versionData.version },
    cacheControl ? { 'Cache-Control': cacheControl } : undefined,
    contentType ? { 'Content-Type': contentType } : undefined
  ])

  const payload = contentType === 'application/json' ? JSON.stringify(data) : data

  const config: AxiosRequestConfig = {
    headers,
    timeout,
    signal: ClientSignal
  }

  // If no signal is provided, fallback to timeout-based abort
  const timeoutId = !ClientSignal
    ? (() => {
        const controller = new AbortController()
        config.signal = controller.signal
        return setTimeout(() => {
          controller.abort()
        }, timeout)
      })()
    : undefined

  return new Promise<T>(async (resolve, reject) => {
    try {
      const response = await fetchData<T>(method, path, config, payload)
      resolve(response)
    } catch (error: any) {
      reject(error)
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  })
}

const fetchData = async <T>(method: AxiosMethod, path: string, config: AxiosRequestConfig<any>, payload: any) => {
  const client = getApiClient()
  return await (method === 'GET' ? client.get<T>(path, config) : client.post<T>(path, payload, config))
}
